import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { authAPI } from '../services/api';
import './Login.css';

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  username?: string;
  password?: string;
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
    rememberMe: false
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState({
    username: false,
    password: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from location state
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Check if user is already logged in - Use window.location instead of navigate
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Use window.location for initial redirect to avoid React Router issues
      window.location.href = from;
    }
  }, [from]);

  // Validation functions
  const validateUsername = (username: string): string => {
    if (!username.trim()) {
      return 'Please enter email or phone number';
    }
    
    if (username.includes('@')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(username)) {
        return 'Please enter a valid email address';
      }
    } else {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,5}[-\s.]?[0-9]{1,5}$/;
      if (!phoneRegex.test(username)) {
        return 'Please enter a valid phone number';
      }
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password.trim()) {
      return 'Please enter your password';
    }
    return '';
  };

  const validateField = (field: keyof FormErrors, value: string): string => {
    switch (field) {
      case 'username': return validateUsername(value);
      case 'password': return validatePassword(value);
      default: return '';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name as keyof FormErrors, formData[name as keyof LoginFormData] as string);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // If touched, validate on change
    if (touched[name as keyof typeof touched]) {
      const error = validateField(name as keyof FormErrors, value);
      setFormErrors(prev => ({ ...prev, [name]: error }));
    }
    
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;
    
    // Validate username/email/phone
    const usernameError = validateUsername(formData.username);
    if (usernameError) {
      errors.username = usernameError;
      isValid = false;
    }
    
    // Validate password
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      errors.password = passwordError;
      isValid = false;
    }
    
    setFormErrors(errors);
    setTouched({
      username: true,
      password: true
    });
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error field
      const firstErrorField = document.querySelector('.zitech-form-input-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (firstErrorField as HTMLElement).focus();
      }
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const response = await authAPI.login(formData.username.trim(), formData.password);
      const data = response.data;
      
      if (data.access) {
        localStorage.setItem('access_token', data.access);
        if (formData.rememberMe) {
          localStorage.setItem('remember_me', 'true');
        }
      }
      
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }
      
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      localStorage.setItem('last_login', new Date().toISOString());
      
      // Use window.location for redirect after login
      window.location.href = from;
      
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response) {
        const { data, status } = err.response;
        
        if (status === 401) {
          setError('Invalid email/phone or password. Please try again.');
        } else if (status === 400) {
          if (data.error) {
            setError(data.error);
          } else if (data.detail) {
            setError(data.detail);
          } else if (data.non_field_errors) {
            setError(data.non_field_errors[0]);
          } else {
            setError('Please check your credentials and try again.');
          }
        } else if (status === 403) {
          setError('Your account is inactive. Please contact support.');
        } else {
          setError('Server error. Please try again later.');
        }
      } else if (err.request) {
        setError('Network error. Please check your internet connection and make sure the server is running.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  const handleRequestService = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/request-service');
  };

  // SVG Icons for validation
  const Icons = {
    Warning: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 9v4M12 17h.01"/>
        <circle cx="12" cy="12" r="10"/>
      </svg>
    ),
  };

  return (
    <>
      <Navbar />
      <div className="zitech-login-page">
        {/* Animated Background Orbs */}
        <div className="zitech-login-orb zitech-login-orb-1"></div>
        <div className="zitech-login-orb zitech-login-orb-2"></div>
        <div className="zitech-login-orb zitech-login-orb-3"></div>
        
        {/* Floating Particles */}
        <div className="zitech-login-particle zitech-login-particle-1"></div>
        <div className="zitech-login-particle zitech-login-particle-2"></div>
        <div className="zitech-login-particle zitech-login-particle-3"></div>
        <div className="zitech-login-particle zitech-login-particle-4"></div>
        <div className="zitech-login-particle zitech-login-particle-5"></div>
        <div className="zitech-login-particle zitech-login-particle-6"></div>
        
        {/* Tech Shapes */}
        <div className="zitech-tech-shape zitech-shape-hex"></div>
        <div className="zitech-tech-shape zitech-shape-triangle"></div>
        <div className="zitech-tech-shape zitech-shape-circle"></div>

        <div className="zitech-login-container">
          <div className="zitech-login-card">
            <div className="zitech-login-header">
              <h1 className="zitech-login-logo">Welcome Back</h1>
              <p className="zitech-login-subtitle">Sign in to access your dashboard</p>
            </div>

            {error && (
              <div className="zitech-error-alert">
                <div className="zitech-error-message">
                  <span className="zitech-error-icon">⚠️</span>
                  {error}
                </div>
              </div>
            )}

            <form className="zitech-login-form" onSubmit={handleSubmit} noValidate>
              <div className="zitech-form-group">
                <label className="zitech-form-label">Email or Phone Number</label>
                <div className="zitech-input-wrapper">
                  <span className="zitech-input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8L12 13L21 8M5 19H19C20.1 19 21 18.1 21 17V7C21 5.9 20.1 5 19 5H5C3.9 5 3 5.9 3 7V17C3 18.1 3.9 19 5 19Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    name="username"
                    className={`zitech-form-input ${touched.username && formErrors.username ? 'zitech-form-input-error' : ''}`}
                    placeholder="Enter your email or phone number"
                    value={formData.username}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    required
                    autoComplete="username"
                    autoFocus
                    aria-invalid={!!formErrors.username}
                    aria-describedby="username-error"
                  />
                </div>
                <small className="zitech-input-hint">Use your email address or phone number to login</small>
                {touched.username && formErrors.username && (
                  <div className="zitech-form-error" id="username-error">
                    <Icons.Warning />
                    {formErrors.username}
                  </div>
                )}
              </div>

              <div className="zitech-form-group">
                <label className="zitech-form-label">Password</label>
                <div className="zitech-input-wrapper">
                  <span className="zitech-input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M18 8C17.5 5 15 3 12 3C8 3 5 5.5 5 9C5 12 6.5 14 8 16L12 21L16 16C17.5 14 19 12 19 9C19 8.5 18.5 8 18 8Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className={`zitech-form-input ${touched.password && formErrors.password ? 'zitech-form-input-error' : ''}`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    required
                    autoComplete="current-password"
                    aria-invalid={!!formErrors.password}
                    aria-describedby="password-error"
                  />
                  <button
                    type="button"
                    className="zitech-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                        <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </button>
                </div>
                {touched.password && formErrors.password && (
                  <div className="zitech-form-error" id="password-error">
                    <Icons.Warning />
                    {formErrors.password}
                  </div>
                )}
              </div>

              <div className="zitech-form-group zitech-checkbox-group">
                <label className="zitech-checkbox-label">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <span>Remember me for 7 days</span>
                </label>
              </div>

              <button type="submit" className="zitech-login-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="zitech-spinner"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="zitech-login-footer">
              <button 
                type="button" 
                className="zitech-link-button" 
                onClick={handleForgotPassword}
              >
                Forgot password?
              </button>
              <span className="zitech-separator">|</span>
              <button 
                type="button" 
                className="zitech-link-button" 
                onClick={handleRequestService}
              >
                Create Account by Requesting a Service
              </button>
            </div>
            
            <div className="zitech-login-info">
              <small>
                <strong>Note:</strong> If you requested a service, your account was automatically created. 
                Check your email for the temporary password or use the password sent to you.
              </small>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Login;